import {Pipe, PipeTransform} from '@angular/core';
import {
    area,
    bbox,
    booleanIntersects,
    buffer,
    cleanCoords,
    clustersDbscan,
    convex,
    featureCollection,
    point,
    polygon,
    simplify,
    union
} from '@turf/turf';
import {Feature, MultiPolygon, Point, Polygon} from 'geojson';
import {Pushpin} from '../classes/pushpin.class';
import {Status} from '../classes/status.class';

export type PushpinCluster = {
    paths: google.maps.LatLngLiteral[];
    options: google.maps.PolygonOptions;
};

type ZoomConfig = {
    /** Upper bound of the zoom levels that map to this bucket (inclusive) */
    maxZoom: number;
    clusteringRadiusKm: number;
    simplifyTolerance: number;
    bufferKm: number;
    pointBufferKm: number;
};

const DEFAULT_COLOR = '#ff0000';

/**
 * Draws rough areas (instead of individual pins) around groups of pushpins that lie close together.
 * Pins are grouped by the colours of the statuses of their parts. A pin with parts of several statuses is part of the
 * area of each of those statuses, so a status never disappears because only one part has it.
 * Smaller areas are drawn on top of larger ones, so an area of a rare status stays visible inside a large area.
 * The result is cached per zoom bucket, so zooming within a bucket or panning does not recalculate anything.
 */
@Pipe({
    name: 'clusterPushpins'
})
export class PushpinClusterPipe implements PipeTransform {
    private readonly zoomConfig = new Map<number, ZoomConfig>([
        [8, {maxZoom: 10, clusteringRadiusKm: 1.5, simplifyTolerance: 0.001, bufferKm: 2, pointBufferKm: 1.0}],
        [11, {maxZoom: 13, clusteringRadiusKm: 1.1, simplifyTolerance: 0.0004, bufferKm: 1, pointBufferKm: 0.75}],
        [14, {maxZoom: 15, clusteringRadiusKm: 0.7, simplifyTolerance: 0.00012, bufferKm: 0.3, pointBufferKm: 0.25}],
        [16, {maxZoom: Infinity, clusteringRadiusKm: 0.3, simplifyTolerance: 0.00005, bufferKm: 0.1, pointBufferKm: 0.08}]
    ]);

    private readonly cache = new Map<string, PushpinCluster[]>();
    private readonly cacheMaxEntries = 30;

    transform(pushpins: Pushpin[] | null | undefined, zoom: number, statuses?: Status[] | null): PushpinCluster[] {
        if (!pushpins?.length) {
            return [];
        }

        const bucket = this.getZoomBucket(zoom);
        const cacheKey = this.buildCacheKey(pushpins, bucket, statuses);
        const cached = this.cache.get(cacheKey);
        if (cached) {
            return cached;
        }

        const config = this.zoomConfig.get(bucket);
        const colorByStatusId = new Map<number, string>();
        statuses?.forEach(status => colorByStatusId.set(status.id, this.normalizeColor(status.color)));

        const pushpinsByColor = new Map<string, Feature<Point>[]>();
        for (const pushpin of pushpins) {
            const lat = +pushpin.lat;
            const lng = +pushpin.lng;
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                continue;
            }
            for (const color of this.partColors(pushpin, colorByStatusId)) {
                const features = pushpinsByColor.get(color);
                if (features) {
                    features.push(point([lng, lat]));
                } else {
                    pushpinsByColor.set(color, [point([lng, lat])]);
                }
            }
        }

        const polygons: { color: string, poly: Feature<Polygon>, area: number }[] = [];
        for (const [color, features] of pushpinsByColor) {
            for (const poly of this.clusterToPolygons(features, config)) {
                polygons.push({color, poly, area: area(poly)});
            }
        }

        // Largest areas first with the lowest zIndex, so smaller areas end up on top and stay visible
        polygons.sort((a, b) => b.area - a.area);
        const out: PushpinCluster[] = polygons.map(({color, poly}, index) => ({
            paths: (poly.geometry.coordinates[0] ?? []).map(([lng, lat]) => ({lat, lng})),
            options: {
                clickable: false,
                fillColor: color,
                fillOpacity: 0.25,
                strokeColor: color,
                strokeOpacity: 0.8,
                strokeWeight: 1,
                zIndex: index + 1
            }
        }));

        this.cacheSet(cacheKey, out);
        return out;
    }

    private clusterToPolygons(features: Feature<Point>[], config: ZoomConfig): Feature<Polygon>[] {
        const clustered = clustersDbscan(featureCollection(features), config.clusteringRadiusKm, {
            units: 'kilometers',
            minPoints: 1
        });

        const clusters = new Map<number, Feature<Point>[]>();
        for (const feature of clustered.features) {
            const clusterId = feature.properties?.cluster;
            if (clusterId === undefined) {
                continue;
            }
            const members = clusters.get(clusterId);
            if (members) {
                members.push(feature);
            } else {
                clusters.set(clusterId, [feature]);
            }
        }

        const polygons: Feature<Polygon>[] = [];
        for (const members of clusters.values()) {
            if (members.length >= 3) {
                const hull = convex(featureCollection(members));
                if (hull) {
                    polygons.push(buffer(hull, config.bufferKm, {units: 'kilometers'}) as Feature<Polygon>);
                }
            } else {
                members.forEach(member => polygons.push(buffer(member, config.pointBufferKm, {units: 'kilometers'}) as Feature<Polygon>));
            }
        }

        const simplified = polygons.map(p => simplify(p, {tolerance: config.simplifyTolerance, highQuality: false}) as Feature<Polygon>);
        return this.mergeIntersectingPolygons(simplified);
    }

    /** Merges polygons that touch each other into one, so overlapping areas do not stack up their fill opacity */
    private mergeIntersectingPolygons(polygons: Feature<Polygon>[]): Feature<Polygon>[] {
        if (polygons.length <= 1) {
            return polygons;
        }

        const parent = polygons.map((_, i) => i);
        const find = (x: number): number => (parent[x] === x ? x : (parent[x] = find(parent[x])));
        const boxes = polygons.map(p => bbox(p));
        const boxesOverlap = (a: number[], b: number[]) => a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];

        for (let i = 0; i < polygons.length; i++) {
            for (let j = i + 1; j < polygons.length; j++) {
                if (boxesOverlap(boxes[i], boxes[j]) && booleanIntersects(polygons[i], polygons[j])) {
                    parent[find(j)] = find(i);
                }
            }
        }

        const groups = new Map<number, Feature<Polygon>[]>();
        polygons.forEach((p, i) => {
            const root = find(i);
            const group = groups.get(root);
            if (group) {
                group.push(p);
            } else {
                groups.set(root, [p]);
            }
        });

        const merged: Feature<Polygon>[] = [];
        for (const group of groups.values()) {
            if (group.length === 1) {
                merged.push(group[0]);
                continue;
            }

            let acc = cleanCoords(group[0]) as Feature<Polygon | MultiPolygon>;
            for (let i = 1; i < group.length; i++) {
                const next = cleanCoords(group[i]) as Feature<Polygon>;
                const united = this.unionTwo(acc, next);
                if (united) {
                    acc = united;
                } else {
                    merged.push(...this.toPolygons(acc));
                    acc = next;
                }
            }
            merged.push(...this.toPolygons(acc));
        }
        return merged;
    }

    private toPolygons(feature: Feature<Polygon | MultiPolygon>): Feature<Polygon>[] {
        return feature.geometry.type === 'MultiPolygon'
            ? feature.geometry.coordinates.map(coords => polygon(coords))
            : [feature as Feature<Polygon>];
    }

    private unionTwo(a: Feature<Polygon | MultiPolygon>, b: Feature<Polygon | MultiPolygon>): Feature<Polygon | MultiPolygon> | null {
        try {
            const united = union(featureCollection([a, b]));
            return united?.geometry ? united : null;
        } catch {
            return null;
        }
    }

    /** The distinct colours of the statuses of all parts of the pushpin */
    private partColors(pushpin: Pushpin, colorByStatusId: Map<number, string>): Set<string> {
        const colors = new Set<string>();
        pushpin.pushpin_parts?.forEach(part => {
            const color = colorByStatusId.get(part.status_id);
            if (color) {
                colors.add(color);
            }
        });
        if (!colors.size) {
            colors.add(DEFAULT_COLOR);
        }
        return colors;
    }

    private normalizeColor(color?: string): string {
        const cleaned = color?.trim();
        if (!cleaned) {
            return DEFAULT_COLOR;
        }
        return cleaned.startsWith('#') ? cleaned : `#${cleaned}`;
    }

    private getZoomBucket(zoom: number): number {
        for (const [bucket, config] of this.zoomConfig) {
            if (zoom <= config.maxZoom) {
                return bucket;
            }
        }
        return [...this.zoomConfig.keys()].pop();
    }

    private buildCacheKey(pushpins: Pushpin[], bucket: number, statuses?: Status[] | null): string {
        let hash = 2166136261;
        const add = (n: number) => {
            hash ^= n | 0;
            hash = Math.imul(hash, 16777619);
        };

        add(bucket);
        for (const pushpin of pushpins) {
            add(pushpin.id);
            add(Math.round(+pushpin.lat * 1e5));
            add(Math.round(+pushpin.lng * 1e5));
            // The colour depends on the statuses of the parts
            pushpin.pushpin_parts?.forEach(part => add(part.status_id));
        }
        statuses?.forEach(status => {
            add(status.id);
            for (let i = 0; i < (status.color?.length ?? 0); i++) {
                add(status.color.charCodeAt(i));
            }
        });

        return `${bucket}|${pushpins.length}|${hash >>> 0}`;
    }

    private cacheSet(key: string, value: PushpinCluster[]): void {
        if (this.cache.size >= this.cacheMaxEntries) {
            this.cache.delete(this.cache.keys().next().value);
        }
        this.cache.set(key, value);
    }
}
